import { DeleteOutlined, EyeOutlined } from "@ant-design/icons";
import { Button, Modal, Space, Table, Tag } from "antd";
import { useState } from "react";
import { ContactStatusSelect } from "./ContactStatusSelect";

const { confirm } = Modal;

export function ContactTable({
  contacts,
  pagination,
  isLoading,
  onTableChange,
  onStatusChange,
  onDelete,
}) {
  const [selectedContact, setSelectedContact] = useState(null);

  const getCategoryLabel = (category) => {
    const labels = {
      general: "Liên hệ chung",
      retreat: "Thông tin khóa tu",
      donation: "Quyên góp",
      volunteer: "Tình nguyện",
      media: "Truyền thông",
      other: "Khác",
    };
    return labels[category] || category;
  };

  const getCategoryColor = (category) => {
    const colors = {
      general: "blue",
      retreat: "green",
      donation: "gold",
      volunteer: "purple",
      media: "cyan",
      other: "default",
    };
    return colors[category] || "default";
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: "Chờ xử lý",
      replied: "Đã trả lời",
      closed: "Đã đóng",
    };
    return labels[status] || status;
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "orange",
      replied: "green",
      closed: "gray",
    };
    return colors[status] || "default";
  };

  const showContactDetail = (contact) => {
    setSelectedContact(contact);
  };

  const handleDelete = (contact) => {
    confirm({
      title: "Xác nhận xóa",
      content: `Bạn có chắc muốn xóa tin nhắn liên hệ từ ${contact.name}?`,
      okText: "Xóa",
      okType: "danger",
      cancelText: "Hủy",
      onOk() {
        onDelete(contact._id);
      },
    });
  };

  const columns = [
    {
      title: "Người liên hệ",
      dataIndex: "name",
      key: "name",
      render: (name, record) => (
        <div>
          <div className="font-medium">{name}</div>
          <div className="text-sm text-gray-500">{record.email}</div>
          <div className="text-sm text-gray-500">{record.phone}</div>
        </div>
      ),
    },
    {
      title: "Loại",
      dataIndex: "category",
      key: "category",
      render: (category) => (
        <Tag color={getCategoryColor(category)}>
          {getCategoryLabel(category)}
        </Tag>
      ),
    },
    {
      title: "Tiêu đề",
      dataIndex: "title",
      key: "title",
      render: (title) => (
        <div className="max-w-xs truncate" title={title}>
          {title}
        </div>
      ),
    },
    {
      title: "Trạng thái",
      key: "status",
      render: (_, record) => (
        <ContactStatusSelect contact={record} onStatusChange={onStatusChange} />
      ),
    },
    {
      title: "Thời gian",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => (date ? new Date(date).toLocaleString("vi-VN") : "N/A"),
    },
    {
      title: "Thao tác",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => showContactDetail(record)}
            title="Xem chi tiết"
          />
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
            title="Xóa"
          />
        </Space>
      ),
    },
  ];

  return (
    <>
      <Table
        columns={columns}
        dataSource={contacts}
        rowKey="_id"
        loading={isLoading}
        pagination={{
          current: pagination.page,
          pageSize: pagination.limit,
          total: pagination.total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} của ${total} mục`,
        }}
        onChange={onTableChange}
        scroll={{ x: 1000 }}
      />

      {/* Contact Detail Modal */}
      <Modal
        title="Chi tiết tin nhắn liên hệ"
        open={!!selectedContact}
        onCancel={() => setSelectedContact(null)}
        footer={[
          <Button key="close" onClick={() => setSelectedContact(null)}>
            Đóng
          </Button>,
        ]}
        width={700}
      >
        {selectedContact && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="font-medium text-gray-700">Họ và tên:</label>
                <p>{selectedContact.name}</p>
              </div>
              <div>
                <label className="font-medium text-gray-700">Email:</label>
                <p>{selectedContact.email}</p>
              </div>
              <div>
                <label className="font-medium text-gray-700">
                  Số điện thoại:
                </label>
                <p>{selectedContact.phone}</p>
              </div>
              <div>
                <label className="font-medium text-gray-700">
                  Loại liên hệ:
                </label>
                <p>
                  <Tag color={getCategoryColor(selectedContact.category)}>
                    {getCategoryLabel(selectedContact.category)}
                  </Tag>
                </p>
              </div>
            </div>

            <div>
              <label className="font-medium text-gray-700">Tiêu đề:</label>
              <p className="font-medium">{selectedContact.title}</p>
            </div>

            <div>
              <label className="font-medium text-gray-700">Nội dung:</label>
              <div className="mt-2 p-3 bg-gray-50 rounded border whitespace-pre-wrap">
                {selectedContact.content}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 pt-4 border-t">
              <div>
                <label className="font-medium text-gray-700">
                  Thời gian gửi:
                </label>
                <p>
                  {new Date(selectedContact.createdAt).toLocaleString("vi-VN")}
                </p>
              </div>
              <div>
                <label className="font-medium text-gray-700">Trạng thái:</label>
                <p>
                  <Tag color={getStatusColor(selectedContact.status)}>
                    {getStatusLabel(selectedContact.status)}
                  </Tag>
                </p>
              </div>
            </div>

            {selectedContact.status === "replied" &&
              selectedContact.repliedAt && (
                <div className="pt-4 border-t">
                  <label className="font-medium text-gray-700">
                    Thời gian trả lời:
                  </label>
                  <p>
                    {new Date(selectedContact.repliedAt).toLocaleString(
                      "vi-VN",
                    )}
                  </p>
                  {selectedContact.replyContent && (
                    <div className="mt-2">
                      <label className="font-medium text-gray-700">
                        Nội dung trả lời:
                      </label>
                      <div className="mt-2 p-3 bg-blue-50 rounded border whitespace-pre-wrap">
                        {selectedContact.replyContent}
                      </div>
                    </div>
                  )}
                </div>
              )}
          </div>
        )}
      </Modal>
    </>
  );
}
